import { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import { Container, Box, Typography } from '@mui/material';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import './cookie-policy.scss';

gsap.registerPlugin(ScrollTrigger);

function CookiePolicy() {
  const heroRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const animateSplitText = (element: HTMLElement) => {
    const text = element.textContent || '';
    element.innerHTML = text
      .split('')
      .map(char => char === ' ' ? ' ' : `<span class="split-char">${char}</span>`)
      .join('');

    const chars = element.querySelectorAll('.split-char');
    gsap.fromTo(chars,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.6,
        stagger: 0.03,
        ease: "power3.out",
        scrollTrigger: {
          trigger: element,
          start: "top 80%",
          toggleActions: "play none none none"
        }
      }
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (heroRef.current) {
        gsap.fromTo(heroRef.current,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 1, ease: "power2.out" }
        );
      }
      if (titleRef.current) {
        animateSplitText(titleRef.current);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Cookie Policy | Fabbri Agricoltura</title>
        <meta name="description" content="Informativa sull'utilizzo dei cookie sul sito Fabbri Agricoltura." />
      </Helmet>

      <Box className="legal-page-agricola" id="top">
        <Box className="legal-hero-agricola" ref={heroRef}>
          <Container maxWidth="lg" className="legal-hero-content">
            <Typography variant="h1" className="legal-hero-title split-text" ref={titleRef}>
              Cookie Policy
            </Typography>
            <Typography className="legal-hero-subtitle">
              Informativa sull'utilizzo dei cookie ai sensi dell'art. 13 del Regolamento UE 2016/679 (GDPR) e della Direttiva 2002/58/CE
            </Typography>
          </Container>
        </Box>

        <Box className="legal-content-section">
          <Container maxWidth="lg">

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">1.</span> Cosa sono i cookie
              </Typography>
              <Typography className="legal-text">
                I cookie sono piccoli file di testo che i siti web salvano sul dispositivo dell'utente (computer, tablet o smartphone) durante la navigazione. Vengono utilizzati per memorizzare informazioni e migliorare l'esperienza di navigazione.
              </Typography>
            </Box>

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">2.</span> Tipologie di cookie utilizzati
              </Typography>

              <Typography variant="h3" className="legal-subsection-title">Cookie tecnici (necessari)</Typography>
              <Typography className="legal-text">
                Questi cookie sono essenziali per il corretto funzionamento del sito e non possono essere disattivati. Non richiedono il consenso dell'utente.
              </Typography>
              <Box className="legal-table">
                <Box className="legal-table-row legal-table-header">
                  <Typography className="legal-table-cell"><strong>Cookie</strong></Typography>
                  <Typography className="legal-table-cell"><strong>Finalità</strong></Typography>
                  <Typography className="legal-table-cell"><strong>Durata</strong></Typography>
                </Box>
                <Box className="legal-table-row">
                  <Typography className="legal-table-cell">cookie_consent</Typography>
                  <Typography className="legal-table-cell">Memorizza le preferenze cookie dell'utente</Typography>
                  <Typography className="legal-table-cell">12 mesi</Typography>
                </Box>
              </Box>

              <Typography variant="h3" className="legal-subsection-title">Cookie analitici (di terze parti)</Typography>
              <Typography className="legal-text">
                Questi cookie vengono utilizzati per raccogliere informazioni statistiche in forma anonima e aggregata sull'utilizzo del sito. Richiedono il consenso dell'utente.
              </Typography>
              <Box className="legal-table">
                <Box className="legal-table-row legal-table-header">
                  <Typography className="legal-table-cell"><strong>Servizio</strong></Typography>
                  <Typography className="legal-table-cell"><strong>Finalità</strong></Typography>
                  <Typography className="legal-table-cell"><strong>Durata</strong></Typography>
                </Box>
                <Box className="legal-table-row">
                  <Typography className="legal-table-cell">Google Analytics 4 (_ga, _ga_*)</Typography>
                  <Typography className="legal-table-cell">Analisi statistica del traffico web</Typography>
                  <Typography className="legal-table-cell">26 mesi</Typography>
                </Box>
                <Box className="legal-table-row">
                  <Typography className="legal-table-cell">Google Tag Manager</Typography>
                  <Typography className="legal-table-cell">Gestione dei tag e degli script</Typography>
                  <Typography className="legal-table-cell">Sessione</Typography>
                </Box>
              </Box>

              <Typography variant="h3" className="legal-subsection-title">Cookie funzionali (di terze parti)</Typography>
              <Typography className="legal-text">
                Questi cookie abilitano funzionalità aggiuntive sul sito.
              </Typography>
              <Box className="legal-table">
                <Box className="legal-table-row legal-table-header">
                  <Typography className="legal-table-cell"><strong>Servizio</strong></Typography>
                  <Typography className="legal-table-cell"><strong>Finalità</strong></Typography>
                  <Typography className="legal-table-cell"><strong>Durata</strong></Typography>
                </Box>
                <Box className="legal-table-row">
                  <Typography className="legal-table-cell">Google Maps</Typography>
                  <Typography className="legal-table-cell">Visualizzazione mappa nella pagina Contatti</Typography>
                  <Typography className="legal-table-cell">6 mesi</Typography>
                </Box>
              </Box>
            </Box>

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">3.</span> Gestione dei cookie
              </Typography>
              <Typography className="legal-text">
                Al primo accesso al sito viene mostrato un banner informativo che permette di accettare o rifiutare i cookie non essenziali. È possibile modificare le proprie preferenze in qualsiasi momento cliccando sul link "Cookie Policy" presente nel footer del sito.
              </Typography>
              <Typography className="legal-text">
                È inoltre possibile gestire i cookie direttamente dalle impostazioni del proprio browser:
              </Typography>
              <ul className="legal-list">
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/it/kb/protezione-antitracciamento-avanzata-firefox-desktop" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/it-it/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
                <li><a href="https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
              </ul>
              <Typography className="legal-text legal-note">
                <strong>Nota:</strong> la disattivazione dei cookie potrebbe compromettere il corretto funzionamento di alcune parti del sito.
              </Typography>
            </Box>

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">4.</span> Trasferimento dati extra-UE
              </Typography>
              <Typography className="legal-text">
                I cookie di Google (Analytics, Tag Manager, reCAPTCHA, Maps) possono comportare il trasferimento di dati verso gli Stati Uniti. Google aderisce al EU-U.S. Data Privacy Framework, garantendo un livello di protezione adeguato ai sensi del GDPR.
              </Typography>
            </Box>

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">5.</span> Diritti dell'utente
              </Typography>
              <Typography className="legal-text">
                Per maggiori informazioni sui propri diritti in materia di protezione dei dati personali, si rimanda alla <a href="/privacy-policy">Privacy Policy</a>.
              </Typography>
            </Box>

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">6.</span> Riferimenti normativi
              </Typography>
              <ul className="legal-list">
                <li>Regolamento UE 2016/679 (GDPR)</li>
                <li>Direttiva 2002/58/CE (ePrivacy)</li>
                <li>Provvedimento del Garante Privacy n. 229 dell'8 maggio 2014</li>
                <li>Linee guida cookie e altri strumenti di tracciamento — 10 giugno 2021</li>
              </ul>
            </Box>

            <Box className="legal-block legal-footer-info">
              <Typography className="legal-text">
                <strong>Fabbri Agricoltura di Fabbri Dr. Francesco</strong><br />
                <a href="https://maps.app.goo.gl/ws26E8mfGKWDVsZL9" target="_blank" rel="noopener noreferrer">Via Per S. Felice, 3 - 41031 Camposanto (MO)</a><br />
                P.IVA 04004150365
              </Typography>
              <Typography className="legal-text legal-update">
                Ultimo aggiornamento: agosto 2026
              </Typography>
            </Box>

          </Container>
        </Box>
      </Box>
    </>
  );
}

export default CookiePolicy;
